import { useEffect, useState } from 'react'
import { supabase } from './supabaseClient'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Stack,
  Avatar,
  IconButton,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  LinearProgress,
} from '@mui/material'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import EmailIcon from '@mui/icons-material/Email'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import EuroIcon from '@mui/icons-material/Euro'
import HomeIcon from '@mui/icons-material/Home'
import FavoriteIcon from '@mui/icons-material/Favorite'
import DashboardIcon from '@mui/icons-material/Dashboard'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import { ListingCard } from './ListingsCommon'

export default function Overview() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [user, setUser] = useState(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const [upcomingViewings, setUpcomingViewings] = useState([])
  const [latestListings, setLatestListings] = useState([])
  const [activeOffers, setActiveOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [profileData, setProfileData] = useState(null)

  useEffect(() => {
    async function fetchUser() {
      const { data } = await supabase.auth.getUser()
      if (data?.user) {
        setUser(data.user)
      }
    }
    fetchUser()
  }, [])

  useEffect(() => {
    if (!user) return

    async function fetchData() {
      setLoading(true)

      try {
        // Fetch profile data
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name, avatar_url')
          .eq('id', user.id)
          .single()

        setProfileData(profile || {})

        // Fetch unread messages count
        const { count: unreadCount } = await supabase
          .from('messages')
          .select('*', { count: 'exact' })
          .eq('user_id', user.id)
          .eq('read', false)

        // Fetch upcoming viewings
        const { data: viewings } = await supabase
          .from('viewings')
          .select('*, listings(*)')
          .eq('user_id', user.id)
          .in('status', ['confirmed', 'accepted'])
          .order('viewing_date', { ascending: true })
          .limit(3)

        // Fetch latest listings for user's notifications
        const { data: notifications } = await supabase
          .from('notifications')
          .select('id')
          .eq('user_id', user.id)

        const notificationIds = notifications?.map((n) => n.id) || []

        const { data: listings } = await supabase
          .from('listings')
          .select('*')
          .is('sold_at', null)
          .order('created_at', { ascending: false })
          .limit(4)

        // Fetch active offers
        const { data: offers } = await supabase
          .from('offers')
          .select('*, listings(*)')
          .eq('user_id', user.id)
          .not('status', 'in', '("declined","revoked")')
          .order('created_at', { ascending: false })
          .limit(3)

        setUnreadCount(unreadCount || 0)
        setUpcomingViewings(viewings || [])
        setLatestListings(listings || [])
        setActiveOffers(offers || [])
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user])

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'success',
      accepted: 'success',
      pending: 'warning',
      proposed: 'info',
    }
    return colors[status] || 'default'
  }

  const formatDate = () => {
    const today = new Date()
    return today.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <LinearProgress />
        <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
          {t('loading')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
        background: 'linear-gradient(180deg, rgba(245,247,250,1) 0%, rgba(255,255,255,1) 100%)',
        minHeight: '100vh',
      }}
    >
      {/* Welcome Header with Glass Effect */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(37,99,235,0.8) 0%, rgba(29,78,216,0.9) 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100%',
            height: '100%',
            opacity: 0.1,
            zIndex: 0,
          }}
        >
          {/* Abstract pattern */}
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="smallGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#smallGrid)" />
          </svg>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          alignItems={{ xs: 'center', sm: 'flex-start' }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Avatar
            src={profileData?.avatar_url}
            sx={{
              width: { xs: 60, sm: 80 },
              height: { xs: 60, sm: 80 },
              bgcolor: 'primary.dark',
              fontSize: { xs: 24, sm: 32 },
              boxShadow: '0 0 0 4px rgba(255,255,255,0.2)',
            }}
          >
            {profileData?.first_name ? profileData.first_name[0] : user?.email?.[0]?.toUpperCase()}
          </Avatar>

          <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, flexGrow: 1 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                fontWeight: 700,
                mb: 1,
              }}
            >
              {t('overview.welcome')}, {profileData?.first_name || user?.email?.split('@')[0]}!
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.9 }}>
              {formatDate()}
            </Typography>
          </Box>

          <Box sx={{ alignSelf: 'flex-start', display: { xs: 'none', md: 'block' } }}>
            <Button
              component={Link}
              to="/discover"
              variant="contained"
              color="secondary"
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                fontSize: '1rem',
                background: 'white',
                color: 'primary.main',
                '&:hover': {
                  background: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              {t('overview.viewAll')}
            </Button>
          </Box>
        </Stack>

        {/* Quick stats row */}
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: { xs: 'center', sm: 'flex-start' },
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Chip
            icon={<CalendarTodayIcon />}
            label={`${upcomingViewings.length} ${t('overview.scheduledViewings')}`}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              px: 1,
              height: 36,
              '& .MuiChip-icon': { color: 'white' },
            }}
          />
          <Chip
            icon={<EuroIcon />}
            label={`${activeOffers.length} ${t('overview.activeOffers')}`}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              px: 1,
              height: 36,
              '& .MuiChip-icon': { color: 'white' },
            }}
          />
          <Chip
            icon={<EmailIcon />}
            label={`${unreadCount} ${t('overview.unreadMessages', { count: unreadCount })}`}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              px: 1,
              height: 36,
              '& .MuiChip-icon': { color: 'white' },
            }}
          />
        </Box>
      </Paper>

      {/* Main Grid Layout */}
      <Grid container spacing={3}>
        {/* Left Column - Viewings & Offers */}
        <Grid item xs={12} md={7} lg={8}>
          {/* Upcoming Viewings */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <HomeIcon sx={{ color: 'primary.main', mr: 1.5 }} />
              <Typography variant="h5" component="h2" fontWeight="600">
                {t('overview.upcomingViewings')}
              </Typography>
            </Box>

            {upcomingViewings.length === 0 ? (
              <Box
                sx={{
                  py: 4,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t('overview.noUpcomingViewings')}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {upcomingViewings.map((viewing) => (
                  <Card
                    key={viewing.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={8}>
                          <Typography variant="h6" fontWeight="medium" noWrap>
                            {viewing.listings.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <CalendarTodayIcon
                              fontSize="small"
                              sx={{ color: 'primary.main', mr: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(viewing.viewing_date).toLocaleString()}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={viewing.status}
                            color={getStatusColor(viewing.status)}
                            sx={{ mt: 1.5 }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={4}
                          sx={{
                            display: 'flex',
                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                          }}
                        >
                          <Button
                            component={Link}
                            to={`/listing/${viewing.listing_id}`}
                            variant="contained"
                            size="small"
                            endIcon={<ChevronRightIcon />}
                          >
                            {t('overview.viewDetails')}
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>

          {/* Active Offers */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EuroIcon sx={{ color: 'primary.main', mr: 1.5 }} />
              <Typography variant="h5" component="h2" fontWeight="600">
                {t('overview.activeOffers')}
              </Typography>
            </Box>

            {activeOffers.length === 0 ? (
              <Box
                sx={{
                  py: 4,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t('overview.noOngoingOffers')}
                </Typography>
                <Button component={Link} to="/discover" variant="outlined" sx={{ mt: 2 }}>
                  {t('discover.gridView')}
                </Button>
              </Box>
            ) : (
              <Stack spacing={2}>
                {activeOffers.map((offer) => (
                  <Card
                    key={offer.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={8}>
                          <Typography variant="h6" fontWeight="medium" noWrap>
                            {offer.listings.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <EuroIcon fontSize="small" sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="h6" fontWeight="medium">
                              {new Intl.NumberFormat('nl-NL').format(offer.offer_price)}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={offer.status}
                            color={getStatusColor(offer.status)}
                            sx={{ mt: 1.5 }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={4}
                          sx={{
                            display: 'flex',
                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                          }}
                        >
                          <Button
                            component={Link}
                            to={`/listing/${offer.listing_id}`}
                            variant="contained"
                            size="small"
                            endIcon={<ChevronRightIcon />}
                          >
                            {t('overview.viewDetails')}
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Right Column - Latest Listings & Messages */}
        <Grid item xs={12} md={5} lg={4}>
          {/* Latest Listings */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100% - 100px)', // Adjust the height to accommodate Messages Card
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FavoriteIcon sx={{ color: 'primary.main', mr: 1.5 }} />
                <Typography variant="h5" component="h2" fontWeight="600">
                  {t('overview.latestListings')}
                </Typography>
              </Box>
              <Button
                component={Link}
                to="/discover"
                size="small"
                endIcon={<ChevronRightIcon />}
                sx={{ px: 1.5 }}
              >
                {t('overview.viewAll')}
              </Button>
            </Box>

            <Stack spacing={2} sx={{ flex: 1, overflow: 'auto' }}>
              {latestListings.slice(0, 2).map((listing) => (
                <Card
                  key={listing.id}
                  component={Link}
                  to={`/listing/${listing.id}`}
                  sx={{
                    display: 'block',
                    textDecoration: 'none',
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    overflow: 'hidden',
                    height: '160px',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundImage: `url(${listing.image_url || '/api/placeholder/400/300'})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                      color: 'white',
                      p: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {listing.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {listing.price ? `€${listing.price.toLocaleString()}` : ''} •{' '}
                      {listing.postcode}
                    </Typography>
                  </Box>
                </Card>
              ))}
            </Stack>
          </Paper>

          {/* Messages Card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              height: '85px',
            }}
          >
            <Box
              sx={{
                p: 2,
                height: '100%',
                background: 'linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%)',
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton sx={{ bgcolor: 'primary.main', color: 'white' }}>
                  <EmailIcon />
                </IconButton>
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {t('overview.unreadMessages', { count: unreadCount })}
                  </Typography>
                </Box>
              </Stack>
              <Button
                component={Link}
                to="/inbox"
                variant="contained"
                size="small"
                endIcon={<ChevronRightIcon />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t('overview.goToInbox')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}
